import styled, { css, down } from "@xstyled/styled-components";
import Player from "@vimeo/player";
import { useEffect, useRef, useMemo, useState, PropsWithChildren } from "react";

import { ResponsiveButtonContainer } from "./ResponsiveButtonContainer";

import { pushAnalyticsEvent } from "@otta/analytics";
import { Button, Heading, Spacing, List, theme } from "@otta/design";
import { palette, pxToRem } from "@otta/design-tokens";

const VideoWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
`;

const VideoContainer = styled.div`
  border-radius: 4;
  aspect-ratio: 16/9;
  overflow: hidden;
  width: 100%;
  position: relative;
  background-color: ${palette.brand.grey};
`;

const VideoSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    ${palette.grayscale.shade50} 25%,
    ${palette.brand.grey} 50%,
    ${palette.grayscale.shade50} 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const AlignLeftList = styled(List)`
  align-self: flex-start;
  min-width: ${pxToRem(700)};
  padding-left: ${pxToRem(16)};
  width: 100%;

  ${down(
    "desktop",
    css`
      min-width: ${pxToRem(500)};
    `
  )};

  ${down(
    "tablet",
    css`
      min-width: ${pxToRem(300)};
    `
  )};
`;

const ContinueButton = styled(Button)`
  text-decoration: none;
  width: 100%;
  min-width: ${pxToRem(200)};
`;

const BackButton = styled(Button)`
  text-decoration: none;
  width: 100%;
  min-width: ${pxToRem(200)};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${pxToRem(16)};
  width: 100%;

  ${down(
    "tablet",
    css`
      flex-direction: column;
    `
  )};
`;

const ContentWrapper = styled.div`
  max-width: ${pxToRem(700)};
`;

interface VideoContentProps {
  title?: string;
  videoUrl: string;
  videoAnalyticsCategory?: string;
  listItems?: string[];
  continueButtonText?: string;
  continueButtonAnalyticsId?: string;
  onContinue?: () => void;
  backButtonText?: string;
  backButtonAnalyticsId?: string;
  onBack?: () => void;
}

export function VideoContent({
  title,
  videoUrl,
  videoAnalyticsCategory,
  listItems,
  continueButtonText,
  continueButtonAnalyticsId,
  onContinue,
  backButtonText,
  backButtonAnalyticsId,
  onBack,
  children,
}: VideoContentProps & PropsWithChildren) {
  const videoRef = useRef<HTMLDivElement>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  const videoCurrentTimeRef = useRef<number>(0);
  const videoDurationRef = useRef<number>(0);

  const calculateVideoTimeData = () => {
    if (!videoDurationRef.current) return null;

    return {
      videoDuration: parseFloat(videoDurationRef.current.toFixed(3)),
      videoCurrentTime: parseFloat(videoCurrentTimeRef.current.toFixed(3)),
      videoCurrentPercent: parseFloat(
        (videoCurrentTimeRef.current / videoDurationRef.current).toFixed(3)
      ),
    };
  };

  const genericTrackingData = useMemo(
    () => ({
      videoId: videoUrl,
      videoCategory: videoAnalyticsCategory || "Non categorised video",
    }),
    [videoUrl, videoAnalyticsCategory]
  );

  useEffect(() => {
    if (!videoRef.current) return;

    setIsVideoLoading(true);

    const player = new Player(videoRef.current, {
      url: videoUrl,
      responsive: true,
    });

    player.getTextTracks().then(tracks => {
      const firstLanguage = tracks[0]?.language;
      if (firstLanguage) {
        player.enableTextTrack(firstLanguage);
      }
    });

    player.on("timeupdate", eventData => {
      videoCurrentTimeRef.current = eventData.seconds;
    });

    player.on("play", eventData => {
      videoDurationRef.current = eventData.duration;

      pushAnalyticsEvent({
        eventName: "Company Recruiter Played Video",
        videoDuration: eventData.duration,
        videoCurrentPercent: eventData.percent,
        videoCurrentTime: eventData.seconds,
        ...genericTrackingData,
      });
    });

    player.on("pause", eventData => {
      pushAnalyticsEvent({
        eventName: "Company Recruiter Paused Video",
        videoDuration: eventData.duration,
        videoCurrentPercent: eventData.percent,
        videoCurrentTime: eventData.seconds,
        ...genericTrackingData,
      });
    });

    player.on("playbackratechange", eventData => {
      pushAnalyticsEvent({
        eventName: "Company Recruiter Video Playback Rate Changed",
        playbackRate: eventData.playbackRate,
        ...genericTrackingData,
      });
    });

    player.on("seeked", eventData => {
      pushAnalyticsEvent({
        eventName: "Company Recruiter Seeked Video",
        videoDuration: eventData.duration,
        videoCurrentPercent: eventData.percent,
        videoCurrentTime: eventData.seconds,
        ...genericTrackingData,
      });
    });

    player.on("ended", eventData => {
      pushAnalyticsEvent({
        eventName: "Company Recruiter Ended Video",
        videoDuration: eventData.duration,
        videoCurrentPercent: eventData.percent,
        videoCurrentTime: eventData.seconds,
        ...genericTrackingData,
      });
    });

    player.ready().finally(async () => {
      // It's important to know that the video can be loaded and displayed before the player is ready,
      // since the player has a reference to the video element and its z-index is higher than the skeleton.
      // But we ensure that the state is up-to-date.
      setIsVideoLoading(false);
    });

    return () => {
      player.destroy();
    };
  }, [videoUrl, genericTrackingData]);

  useEffect(() => {
    const trackEndedVideo = () => {
      window.removeEventListener("beforeunload", trackEndedVideo);

      const videoTimeData = calculateVideoTimeData();

      if (videoTimeData === null || videoTimeData.videoCurrentTime <= 0.0)
        return;

      pushAnalyticsEvent({
        eventName: "Company Recruiter Ended Video",
        ...videoTimeData,
        ...genericTrackingData,
      });
    };

    window.addEventListener("beforeunload", trackEndedVideo);

    return trackEndedVideo;
  }, [genericTrackingData]);

  return (
    <Spacing size={2}>
      {title && (
        <Heading size={3} bold align="center">
          {title}
        </Heading>
      )}
      <VideoWrapper>
        <VideoContainer ref={videoRef} data-testid="video-container">
          {isVideoLoading && <VideoSkeleton />}
        </VideoContainer>
      </VideoWrapper>
      {listItems && listItems.length > 0 && (
        <ContentWrapper>
          <AlignLeftList type="bullet" size={0}>
            {listItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </AlignLeftList>
        </ContentWrapper>
      )}

      {children && <ContentWrapper>{children}</ContentWrapper>}

      {(onContinue || onBack) && (
        <ResponsiveButtonContainer breakpoint={theme.screens.desktop}>
          <Spacing size={-1.5}>
            <ButtonContainer>
              {onBack && backButtonText && (
                <BackButton
                  onClick={onBack}
                  level="secondary"
                  data-analytics-id={backButtonAnalyticsId}
                >
                  {backButtonText}
                </BackButton>
              )}
              {onContinue && continueButtonText && (
                <ContinueButton
                  onClick={onContinue}
                  level="primary"
                  data-analytics-id={continueButtonAnalyticsId}
                >
                  {continueButtonText}
                </ContinueButton>
              )}
            </ButtonContainer>
          </Spacing>
        </ResponsiveButtonContainer>
      )}
    </Spacing>
  );
}
